import { forwardRef, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes, type SelectHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  rightAdornment?: ReactNode;
  className?: string;
}

const baseField =
  'w-full rounded-lg border bg-surface text-ink placeholder:text-ink-subtle ' +
  'transition focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/60 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

function Wrapper({
  label,
  hint,
  error,
  required,
  className,
  children,
}: FieldProps & { children: ReactNode }) {
  return (
    <label className={cn('block', className)}>
      {label && (
        <span className="mb-1.5 inline-flex items-center gap-1 text-sm font-medium text-ink">
          {label}
          {required && <span className="text-danger">*</span>}
        </span>
      )}
      {children}
      {hint && !error && <p className="mt-1.5 text-xs text-ink-subtle">{hint}</p>}
      {error && <p className="mt-1.5 text-xs text-danger">{error}</p>}
    </label>
  );
}

interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    FieldProps {}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, iconLeft, iconRight, rightAdornment, className, type = 'text', ...rest },
  ref,
) {
  const hasIcon = Boolean(iconLeft || iconRight || rightAdornment);
  const field = (
    <input
      ref={ref}
      type={type}
      required={required}
      className={cn(
        baseField,
        'h-10 px-3.5 text-sm',
        iconLeft && 'pl-10',
        (iconRight || rightAdornment) && 'pr-10',
        error ? 'border-danger/60 focus:ring-danger/30 focus:border-danger' : 'border-border',
      )}
      {...rest}
    />
  );

  return (
    <Wrapper label={label} hint={hint} error={error} required={required} className={className}>
      {hasIcon ? (
        <div className="relative">
          {iconLeft && (
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-subtle">
              {iconLeft}
            </span>
          )}
          {field}
          {iconRight && (
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-ink-subtle">
              {iconRight}
            </span>
          )}
          {rightAdornment && (
            <span className="absolute inset-y-0 right-2 flex items-center">{rightAdornment}</span>
          )}
        </div>
      ) : (
        field
      )}
    </Wrapper>
  );
});

interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement>,
    FieldProps {}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, required, className, rows = 4, ...rest },
  ref,
) {
  return (
    <Wrapper label={label} hint={hint} error={error} required={required} className={className}>
      <textarea
        ref={ref}
        rows={rows}
        required={required}
        className={cn(
          baseField,
          'px-3.5 py-2.5 text-sm leading-relaxed resize-y',
          error ? 'border-danger/60 focus:ring-danger/30 focus:border-danger' : 'border-border',
        )}
        {...rest}
      />
    </Wrapper>
  );
});

interface SelectProps
  extends SelectHTMLAttributes<HTMLSelectElement>,
    FieldProps {
  options?: Array<{ value: string; label: string; disabled?: boolean }>;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, required, className, options, children, ...rest },
  ref,
) {
  return (
    <Wrapper label={label} hint={hint} error={error} required={required} className={className}>
      <select
        ref={ref}
        required={required}
        className={cn(
          baseField,
          'h-10 px-3 text-sm pr-9 appearance-none bg-no-repeat bg-[right_0.75rem_center] bg-[length:0.7rem]',
          "bg-[url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%237C8A8E'><path fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 011.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z' clip-rule='evenodd' /></svg>\")]",
          error ? 'border-danger/60 focus:ring-danger/30 focus:border-danger' : 'border-border',
        )}
        {...rest}
      >
        {options
          ? options.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))
          : children}
      </select>
    </Wrapper>
  );
});

export const Checkbox = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }>(
  function Checkbox({ className, label, ...rest }, ref) {
    return (
      <label className={cn('inline-flex items-start gap-2.5 cursor-pointer select-none', className)}>
        <input
          ref={ref}
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary/40"
          {...rest}
        />
        <span className="text-sm text-ink leading-snug">{label}</span>
      </label>
    );
  },
);
